import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { History, Loader2 } from 'lucide-react';
import { messengerApi, type MessengerEditHistory } from '../lib/messengerApi';
import { formatTime } from '../utils/format';

interface Props {
  messageId: string;
  editedAt: string | null;
}

// Task #1660 — tooltip "modificato" → on-click apre popover con la
// cronologia versioni del messaggio. Lazy fetch al primo open.

export function EditHistoryTooltip({ messageId, editedAt }: Props) {
  const [data, setData] = useState<MessengerEditHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (v && !data && !loading) {
      setLoading(true);
      messengerApi.editHistory(messageId)
        .then((res) => { if (res.success && res.data) setData(res.data); })
        .finally(() => setLoading(false));
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] underline-offset-2 hover:underline text-current opacity-80"
          title={editedAt ? `Modificato ${formatTime(editedAt)}` : 'Modificato'}
        >
          mod.
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-80 p-3">
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold">
          <History className="h-3.5 w-3.5 text-blue-500" />
          Cronologia modifiche
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Caricamento…
          </div>
        )}
        {!loading && data && (
          <div className="space-y-2 max-h-64 overflow-auto">
            {data.history.length === 0 && (
              <p className="text-xs text-muted-foreground italic">Nessuna versione precedente registrata.</p>
            )}
            {data.history.map((h) => (
              <div key={h.id} className="text-xs border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                <div className="text-[10px] text-muted-foreground">
                  {h.editor_first_name || ''} {h.editor_last_name || ''} · {formatTime(h.edited_at)}
                </div>
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {h.prev_content || <span className="italic text-muted-foreground">(vuoto)</span>}
                </div>
              </div>
            ))}
            <div className="text-xs border-l-2 border-blue-400 pl-2 pt-1">
              <div className="text-[10px] text-blue-500 font-semibold">VERSIONE ATTUALE</div>
              <div className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                {data.current_content}
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
